import { z } from 'zod';

import { apiGet, apiPatch, apiPost } from '../client';

export const ClassSessionRecordSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  serviceId: z.string(),
  instructorId: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  capacity: z.number(),
  resourceIds: z.array(z.string()),
  priceCents: z.number(),
  status: z.enum(['published', 'cancelled']),
  version: z.number(),
  availableCapacity: z.number(),
  policy: z.object({
    cancellationCutoffMin: z.number(),
    paymentMode: z.enum(['pay_at_studio', 'prepay_required']),
  }).optional(),
});
export type ClassSessionRecord = z.infer<typeof ClassSessionRecordSchema>;

const ListSchema = z.object({ data: z.array(ClassSessionRecordSchema) });

export type CreateSessionInput = {
  serviceId: string;
  instructorId: string;
  startsAt: string;
  endsAt: string;
  capacity: number;
  resourceIds: string[];
  priceCents: number;
  policy: { cancellationCutoffMin: number; paymentMode: 'pay_at_studio' | 'prepay_required' };
};

/**
 * The business's own schedule in [from, to), cancelled classes included and before
 * publication too. Business identity comes from the session, never the caller.
 * The API caps the window at 31 days.
 */
export function listSessions(from: string, to: string): Promise<{ data: ClassSessionRecord[] }> {
  const query = new URLSearchParams({ from, to });
  return apiGet(`/v1/business/sessions?${query}`, ListSchema);
}

export function createSession(input: CreateSessionInput, idempotencyKey: string): Promise<ClassSessionRecord> {
  return apiPost('/v1/business/sessions', ClassSessionRecordSchema, input, idempotencyKey);
}

export function updateSession(sessionId: string, input: Partial<CreateSessionInput>, idempotencyKey: string): Promise<ClassSessionRecord> {
  return apiPatch(`/v1/business/sessions/${encodeURIComponent(sessionId)}`, ClassSessionRecordSchema, input, idempotencyKey);
}

export function cancelSession(sessionId: string, idempotencyKey: string): Promise<ClassSessionRecord> {
  return apiPost(`/v1/business/sessions/${encodeURIComponent(sessionId)}/cancel`, ClassSessionRecordSchema, {}, idempotencyKey);
}
